import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  linkedSignal,
  model,
  output,
  signal,
  untracked,
  ViewEncapsulation,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';

import { FieldTree, form, FormField } from '@angular/forms/signals';

import {
  EditModeDirective,
  InlineEditorComponent,
  ProfileCardComponent,
  ProfileCardSizeEnum,
  ViewModeDirective,
  compareObjectsFn,
} from '@erm/cluster';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import {
  getErrorDisplayMessage,
  CONTACT_WRAPPER_DEFAULT,
  contactFormSchema,
  ContactInfo,
  ContactWrapper,
  determineContactWrapperDefault,
} from '@data-access/profiles';

import { MatMenuModule } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  TemplateTypeValidatorDirective,
  WcgButtonComponent,
  WcgMenuOption,
} from '@wcg/frontend-framework';
import { MatCheckbox } from '@angular/material/checkbox';
import { PhoneFormatPipe } from '@shared/pipes/phone-format.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProfileCardComponent,
    InlineEditorComponent,
    EditModeDirective,
    ViewModeDirective,
    MatMenuModule,
    MatIcon,
    NgTemplateOutlet,
    NgClass,
    MatInputModule,
    MatSelectModule,
    FormField,
    WcgButtonComponent,
    TemplateTypeValidatorDirective,
    MatCheckbox,
    NgxSkeletonLoaderComponent,
    PhoneFormatPipe,
  ],
  providers: [PhoneFormatPipe],
  selector: 'wcg-contact-wrapper',
  styleUrls: ['./contact-wrapper.component.css'],
  templateUrl: './contact-wrapper.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class ContactWrapperComponent {
  readonly compareObjectsFn = compareObjectsFn;
  readonly title = input<string>('');
  readonly label = input<string>('');

  readonly assignedContacts = input<ContactInfo[]>();
  readonly placeholder = input<string>('');
  readonly maxLength = input<number>(50);
  readonly contactType = input<'email' | 'phone'>('phone');
  readonly countryCodes = input<WcgMenuOption[]>([]);
  readonly canEdit = input<boolean>(false);
  readonly isInternal = input<boolean>(false);
  readonly cardSize = input<ProfileCardSizeEnum>(ProfileCardSizeEnum.Medium);
  readonly icon = input<string>('');
  readonly loading = input<boolean>(false);
  readonly error = model<Error | null>(null);

  readonly $setAddButton = signal<boolean>(false);
  readonly $currentEditableIndex = signal<number | null>(null);

  readonly displayErrorMessage = output<string>();
  readonly saveContact = output<ContactInfo[]>();

  /**
   *
   * @NOTE Template Type Annotations
   */

  readonly contactNumberTemplate!: {
    props: {
      isLast: boolean;
      formField: FieldTree<ContactInfo>;
    };
  };

  readonly emailTemplate!: {
    props: {
      formField: FieldTree<ContactInfo>;
    };
  };
  readonly KebabMenuTemplate!: {
    props: {
      index: number;
      formField: FieldTree<ContactInfo>;
    };
  };

  readonly actionButtonTemplate!: {
    props: {
      isDisabled: boolean;
      index: number;
      isAddContact: boolean;
      contactInfo: ContactInfo;
    };
  };

  /**
   * @END Template Type Annotations
   */

  /**
   * @Note Signal Form
   */

  /**
   * @NOTE we will need to define a signal to pass into signal form
   */
  private readonly _contactInfoSignal = linkedSignal({
    computation: (assignedContacts) => {
      if (!assignedContacts?.length) {
        return {
          ...CONTACT_WRAPPER_DEFAULT,
          contactForm: determineContactWrapperDefault(this.contactType()),
        };
      }
      return {
        contactForm: determineContactWrapperDefault(this.contactType()),
        assignedContacts: [...assignedContacts],
      };
    },
    source: this.assignedContacts,
  });

  contactInfoSignalForm = form<ContactWrapper>(
    this._contactInfoSignal,
    contactFormSchema,
  );
  /**
   * @END Signal Form
   */

  constructor() {
    /*
     * @NOTE:
     * Will need to use effect to handle signal states separately. For different concerns
     * */
    effect(() => {
      if (
        this.$setAddButton() &&
        this.contactInfoSignalForm.contactForm.countryCode().invalid()
      ) {
        this.contactInfoSignalForm.contactForm.countryCode().markAsTouched();
        this.contactInfoSignalForm.contactForm.countryCode().markAsDirty();
      } else {
        this.contactInfoSignalForm.contactForm.countryCode().reset();
      }
    });

    effect(() => {
      const _error = this.error();
      if (!_error) {
        return;
      }
      untracked(() => {
        this._contactInfoSignal.update((contactInfo) => ({
          ...contactInfo,
          assignedContacts: [...(this.assignedContacts() ?? [])],
        }));
        this.$currentEditableIndex.set(null);
        this.$setAddButton.set(false);
      });
    });
  }

  deleteContact(contact: ContactInfo, index: number): void {
    if (this.$currentEditableIndex() === index) {
      this.$currentEditableIndex.set(null);
    }

    if (this._validatePrimaryContact(index, 'delete')) {
      this._displayPrimaryContactErrorMessage('delete');
      return;
    }

    this._contactInfoSignal.update((contactInfo) => ({
      ...contactInfo,
      assignedContacts: contactInfo.assignedContacts.filter(
        (c) => c.id !== contact.id,
      ),
    }));

    this._emitSavedContacts();
  }

  addNewContact(contactInfo: ContactInfo): void {
    /*
     * @NOTE: At least one primary contact is required. If there is no primary contact assigned, the new contact must be a primary contact. If not, we will need to reset to its original assigned contacts.
     * */
    const _primaryContact = this._contactInfoSignal().assignedContacts.filter(
      (c) => c.isPrimary,
    ).length;
    if (_primaryContact < 1 && !contactInfo.isPrimary) {
      this._displayPrimaryContactErrorMessage('add');
      return;
    }

    if (this.contactType() === 'phone' && contactInfo.isPrimary) {
      this._handlePrimaryContactChange(contactInfo, -1);
    } else {
      this._updateAssignedContacts(contactInfo);
    }

    this._emitSavedContacts();
    this.$setAddButton.set(false);
    this.contactInfoSignalForm().reset();
  }

  handleNewContactCancel(): void {
    this.$setAddButton.set(false);
    this._resetToInitialState();
  }

  handleAddNewContact(): void {
    this.$setAddButton.set(true);
    this.$currentEditableIndex.set(null);
    this._resetToInitialState();
  }

  handleEditContactCancel(): void {
    this._contactInfoSignal.update((contactInfo) => ({
      ...contactInfo,
      assignedContacts: [...(this.assignedContacts() ?? [])],
    }));
    this.$currentEditableIndex.set(null);
  }

  handleEditContact(
    previousIndex: number | null,
    currentIndex: number,
    previousContactId: string | null | undefined,
  ) {
    /**
     * If there is no previously active edit (null or undefined),
     * simply open the new contact for editing and return early.
     * No discard logic is needed because there is nothing previously in-flight.
     * we want to do an explicit null check here because 0 is a valid index that we don't want to short-circuit on.
     */
    if (previousIndex === null || previousIndex === undefined) {
      this.$currentEditableIndex.set(currentIndex);
      return;
    }

    /**
     * The user is switching from one contact row to a different one while the
     * form is dirty (i.e. they made changes but never saved/cancelled).
     * We silently discard those unsaved edits and restore the previous row
     * back to its original server-provided values so the list stays consistent.
     */
    if (
      previousIndex &&
      previousIndex !== currentIndex &&
      this.contactInfoSignalForm().dirty()
    ) {
      /**
       * Guard: we need the original ID to confirm the contact at that index
       * hasn't been reordered since the edit began (e.g. a primary-contact
       * promotion could shift positions).
       */
      if (previousContactId) {
        const _previousContact = this.assignedContacts()?.[previousIndex];

        /**
         * Only restore if the contact at that position still matches the
         * original ID.  If it has moved, skip the restore to avoid writing
         * stale data into the wrong slot.
         */
        if (_previousContact && _previousContact.id === previousContactId) {
          /**
           * Overwrite the in-memory signal at the previous index with the
           * original input value, effectively undoing any unsaved edits.
           */
          this._contactInfoSignal.update((contacts) => {
            const _assignedContacts = [...contacts.assignedContacts];
            _assignedContacts[previousIndex] = _previousContact!;
            return {
              ...contacts,
              assignedContacts: _assignedContacts,
            };
          });
          /** Reset form dirty/touched state so the newly opened row starts clean. */
          this.contactInfoSignalForm().reset();
        }
      }
    }
    /** Move edit focus to the row the user actually clicked. */
    this.$currentEditableIndex.set(currentIndex);
  }

  saveContactInfo(contactIndex: number): void {
    if (contactIndex < 0) {
      this.$currentEditableIndex.set(null);
      return;
    }
    if (this.contactType() === 'phone') {
      if (this._validatePrimaryContact(contactIndex, 'edit')) {
        this._displayPrimaryContactErrorMessage('edit');
        return;
      }

      const _contactInfo =
        this.contactInfoSignalForm.assignedContacts[contactIndex]().value();
      if (_contactInfo.isPrimary) {
        this._handlePrimaryContactChange(_contactInfo, contactIndex);
      }
    }

    this._emitSavedContacts();
    this.$currentEditableIndex.set(null);
  }

  private _handlePrimaryContactChange(
    contact: ContactInfo,
    contactIndex: number,
  ): void {
    this._contactInfoSignal.update((contactInfo) => {
      const _assignedContacts = contactInfo.assignedContacts
        .filter((_, cIndex) => cIndex !== contactIndex)
        .map((contact) => ({
          ...contact,
          isPrimary: false,
        }));

      return {
        ...contactInfo,
        contactForm: determineContactWrapperDefault(this.contactType()),
        assignedContacts: [contact, ..._assignedContacts],
      };
    });
  }

  private _updateAssignedContacts(contact: ContactInfo): void {
    this._contactInfoSignal.update((contactInfo) => ({
      contactForm: determineContactWrapperDefault(this.contactType()),
      assignedContacts: [...contactInfo.assignedContacts, contact],
    }));
  }
  private _emitSavedContacts(): void {
    if (this.contactType() === 'phone') {
      this.saveContact.emit([
        ...this.contactInfoSignalForm.assignedContacts().value(),
      ]);
    } else {
      this.saveContact.emit(
        this.contactInfoSignalForm.assignedContacts().value(),
      );
    }
  }

  private _displayPrimaryContactErrorMessage(
    action: 'add' | 'edit' | 'delete',
  ): void {
    /*
     * @NOTE: We will need to reset to its original assigned contacts.
     * */
    this._contactInfoSignal.update((contactInfo) => ({
      ...contactInfo,
      assignedContacts: [...(this.assignedContacts() ?? [])],
    }));

    this.displayErrorMessage.emit(
      getErrorDisplayMessage(action, this.contactType()),
    );
  }

  private _validatePrimaryContact(
    contactIndex: number,
    action: 'edit' | 'delete',
  ) {
    const primaryCount = this._contactInfoSignal().assignedContacts.filter(
      (c, cIndex) => c.isPrimary && cIndex !== contactIndex,
    ).length;
    const _currentContactPrimary =
      this.contactInfoSignalForm.assignedContacts[contactIndex]().value()
        .isPrimary;

    if (action === 'edit') {
      return primaryCount < 1 && !_currentContactPrimary;
    }
    return primaryCount < 1 || _currentContactPrimary;
  }

  private _resetToInitialState(): void {
    this._contactInfoSignal.update((contactInfo) => {
      const _contactInfo = this.contactInfoSignalForm().dirty()
        ? {
            ...contactInfo,
            assignedContacts: [...(this.assignedContacts() ?? [])],
          }
        : contactInfo;
      return {
        ..._contactInfo,
        contactForm: determineContactWrapperDefault(this.contactType()),
      };
    });
    this.contactInfoSignalForm().reset();
  }
}
